import React from 'react';
import isEqual from 'lodash/isEqual';
import {Map} from 'immutable';
import {checkoutInputStyle, mobileCheckoutInputStyle} from './styles';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {STRIPE_ELEMENT_TYPE} from '@packages/systems/commerce/constants';
import {
  type AtomConfig,
  withSystem,
  Plugins,
  Atom,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {TRecord} from '@packages/systems/wfdl-ast';
import StyleMapObserver, {AppliedStylesMap} from './StyleMapObserver';
import {FOCUS_COMBO_CLASS} from '@packages/systems/core/utils/StyleUtils';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  useStylesMap,
  useSiteSearchIndexMode,
  useRuntimeEnvironment,
} from '@packages/systems/render';

type StripeElementType = 'cardNumber' | 'cardExpiry' | 'cardCvc';

// Stripe elements have a `disabled` property that can be passed to element.create or element.update:
// https://github.com/stripe/react-stripe-elements/issues/136#issuecomment-404015039
// However, we still need to render a mask over the whole element in order to capture click events in "design mode".
// Without the mask, Stripe iframe children would capture the events, making them non selectable in the Designer
const maskStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
} as const;

const stylePositionRelative = Map({style: Map({position: 'relative'})});

const getStripe = (element?: Element | null) => {
  if (!element) {
    return null;
  }

  const doc = element.ownerDocument;
  const win = doc.defaultView;
  // @ts-expect-error - TS18047 - 'win' is possibly 'null'. | TS2339 - Property 'Stripe' does not exist on type 'Window & typeof globalThis'. | TS18047 - 'win' is possibly 'null'. | TS2339 - Property 'Stripe' does not exist on type 'Window & typeof globalThis'.
  return win.Stripe ? win.Stripe : null;
};

const stripeElementFactory = (elementType: StripeElementType) => {
  const CommerceCheckoutStripeElementFC = (
    props: AtomRenderProps & {
      createStyleMapObserver: (element?: Element | null) => void;
      setElement: (element?: Element | null) => void;
    }
  ) => {
    const styles = useStylesMap(props.elementId);
    const runtimeEnvironment = useRuntimeEnvironment();

    const isPublishedSite = runtimeEnvironment === WebflowEnvironments.SSR;
    const isPreviewMode = props.environmentContext.get('isPreviewMode');

    const stampedAttributes = props.attributes.merge({
      [STRIPE_ELEMENT_TYPE]: elementType,
      'data-wf-style-map': JSON.stringify(styles || {}),
    });

    const styledAttributes =
      isPreviewMode || isPublishedSite
        ? stampedAttributes
        : stampedAttributes.mergeDeep(stylePositionRelative);

    return (
      <div
        {...props.generateAttributes(styledAttributes, props.classes)}
        ref={props.createStyleMapObserver}
      >
        <div ref={props.setElement} />
        {isPreviewMode || isPublishedSite ? null : <div style={maskStyles} />}
      </div>
    );
  };

  class CommerceCheckoutStripeElement extends React.Component<{
    atomProps: AtomRenderProps;
    siteSearchIndexMode: boolean;
  }> {
    stripeElement: any;
    element: Element | null | undefined;
    designContainerElement: Element | null | undefined;
    styleMapObserver: StyleMapObserver | null | undefined;
    warned = false;
    mountingTimeout = null;

    componentDidMount = () => {
      const Stripe = getStripe(this.element);

      if (Stripe) {
        this.renderStripeElement(
          Stripe,
          this.props.atomProps.context.get('ecommPublishableKey')
        );
      } else {
        if (this.mountingTimeout && !this.warned) {
          console.error('Stripe API is not available');
          this.warned = true;
        }
        // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'null'.
        this.mountingTimeout = setTimeout(this.componentDidMount, 1000);
      }
    };

    componentWillUnmount = () => {
      if (this.mountingTimeout) {
        clearTimeout(this.mountingTimeout);
      }

      this.styleMapObserver && this.styleMapObserver.destroy();
    };

    componentDidUpdate = (prevProps: {
      atomProps: AtomRenderProps;
      siteSearchIndexMode: boolean;
    }) => {
      if (!isEqual(prevProps.atomProps.data, this.props.atomProps.data)) {
        const Stripe = getStripe(this.element);

        if (Stripe) {
          this.renderStripeElement(
            Stripe,
            this.props.atomProps.context.get('ecommPublishableKey')
          );
        } else if (this.warned === false) {
          console.error('Stripe API is not available');
          this.warned = true;
        }
      }
    };

    renderStripeElement = (Stripe: any, stripeToken?: string) => {
      if (!(this.element && this.element.ownerDocument.defaultView)) {
        console.error('Stripe elements require a browser runtime.');
        return;
      }
      if (!stripeToken) {
        if (process.env.NODE_ENV === 'development') {
          // TODO: Hide element from Add Tab if Stripe has not been connected
          console.error('Please connect the Stripe account in the Dashboard');
        }
        return;
      }

      if (!this.stripeElement) {
        const stripe = Stripe(stripeToken, {apiVersion: '2020-03-02'});
        const elements = stripe.elements();
        this.stripeElement = elements.create(elementType, {
          style: this.styleMapObserver
            ? StyleMapObserver.appliedStylesToStripeElementStyles(
                this.styleMapObserver.getAppliedStyles()
              )
            : {},
        });

        this.stripeElement.mount(this.element);

        this.stripeElement.on('focus', this.handleStripeFocus);
        this.stripeElement.on('blur', this.handleStripeBlur);
      }
    };

    setElement = (element?: Element | null) => {
      this.element = element;
    };

    handleStripeFocus = () => {
      this.designContainerElement &&
        this.designContainerElement.classList.add(FOCUS_COMBO_CLASS);
    };
    handleStripeBlur = () => {
      this.designContainerElement &&
        this.designContainerElement.classList.remove(FOCUS_COMBO_CLASS);
    };

    createStyleMapObserver = (element?: Element | null) => {
      if (element) {
        this.styleMapObserver = new StyleMapObserver(element, {
          onChange: this.syncStylesToStripeElement,
        });

        this.designContainerElement = element;
      }
    };

    syncStylesToStripeElement = (
      appliedStyles: undefined | AppliedStylesMap
    ) => {
      if (!this.stripeElement) {
        return;
      }

      this.stripeElement.update({
        style:
          StyleMapObserver.appliedStylesToStripeElementStyles(appliedStyles),
      });
    };

    render() {
      return (
        <CommerceCheckoutStripeElementFC
          {...this.props.atomProps}
          setElement={this.setElement}
          createStyleMapObserver={this.createStyleMapObserver}
        />
      );
    }
  }

  return Atom.createRender(function CommerceCheckoutStripeElementWrapper(
    props: AtomRenderProps
  ) {
    const siteSearchIndexMode = useSiteSearchIndexMode();
    return (
      <CommerceCheckoutStripeElement
        atomProps={props}
        siteSearchIndexMode={siteSearchIndexMode}
      />
    );
  });
};

/**
 * Stripe has their own mechanism for support custom fonts.
 * When stripe is provided a lineHeight, it also sets the height of
 * the input to the provided lineHeight. This is different than how
 * lineHeight traditionally works so for now we aren't supporting it.
 */
const SUPPORTED_STRIPE_CSS_PROPERTIES = [
  'color',
  'fontFamily',
  'fontSize',
  'fontSmoothing',
  'fontStyle',
  'letterSpacing',
  'textAlign',
  'textDecoration',
  'textShadow',
  'textTransform',
];

const configFactory: (arg1: StripeElementType) => AtomConfig = (
  elementType
) => {
  const displayNames = {
    cardExpiry: 'Expiration Date',
    cardNumber: 'Card Number',
    cardCvc: 'Security Code',
  } as const;
  return createIntegratedAtom({
    displayName: displayNames[elementType],
    icon: {
      type: 'Component',
      value: qualify(Plugins.name('Form'), iconName('Input')),
    },
    fields: {
      search: withSystem(
        {private: true},
        TRecord({
          exclude: excludeSearchByDefaultType,
        })
      ),
    },
    showEmpty: false,
    constraints: {
      ancestors: [
        {is: 'CommerceCheckoutPaymentInfoWrapper', rule: 'ExactlyOne'},
      ],
    },
    // `displayFlex` is not part of `WebflowCssProperties`, but is used by `StylePanelLayoutSection`
    // to disable one specific display type

    lockedStyles: {
      lineHeight: {
        when: true,
        message: "Credit Card inputs don't support line-height settings.",
      },
      fontWeight: {
        when: true,
        message: "Credit Card inputs don't support font-weight settings.",
      },
      // @ts-expect-error - TS2322 - Type '{ lineHeight: { when: true; message: string; }; fontWeight: { when: true; message: string; }; displayFlex: { when: true; message: string; }; }' is not assignable to type 'Partial<Record<keyof WebflowCssProperties, LockedStyleType>>'.
      displayFlex: {
        when: true,
        message: "Credit Card inputs don't support flexbox.",
      },
    },
    style: {
      base: {
        main: {
          ...checkoutInputStyle,
          noPseudo: {
            ...checkoutInputStyle.noPseudo,
            cursor: 'text',
          },
        },
        tiny: mobileCheckoutInputStyle,
      },
    },
    params: {
      styles: {
        type: 'StylesMap',
        excludeInherited: true,
        filterProperties: SUPPORTED_STRIPE_CSS_PROPERTIES,
      },
    },
    headScript: 'https://js.stripe.com/v3/',
    render: stripeElementFactory(elementType),
  });
};

export default configFactory;
